import { useCallback, useRef, useState } from 'react';
import { Loader2, Search, UserMinus, UserPlus } from 'lucide-react';
import {
  isSingleEventActivity,
  type ActivityAttendance,
  type ActivitySessionDetail,
  type Participant,
} from 'shared';
import CreateParticipantModal from '@/components/CreateParticipantModal';
import { useAddAttendance, useRemoveAttendance } from '@/hooks/useActivities';
import { useParticipants } from '@/hooks/useParticipants';
import { formatDayTime, spansDays } from '@/lib/activityTime';
import { ConfirmDialog } from './ActivityUi';

const SEARCH_RESULTS = 8;

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}

interface SessionRosterCardProps {
  session: ActivitySessionDetail;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

/**
 * Who is present. The list fills in on its own as participants scan the code;
 * staff add by hand anyone who cannot scan, register on the spot anyone who
 * has never signed up (no smartphone, say), and remove anyone who should not
 * be there.
 */
export default function SessionRosterCard({ session, onSuccess, onError }: SessionRosterCardProps) {
  const add = useAddAttendance();
  const remove = useRemoveAttendance();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [removing, setRemoving] = useState<ActivityAttendance | null>(null);
  const [registering, setRegistering] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value.trim()), 350);
  }, []);

  const searching = debouncedSearch.length >= 2;
  const resultsQuery = useParticipants({
    limit: SEARCH_RESULTS,
    search: searching ? debouncedSearch : '',
  });

  const present = new Set(session.attendances.map((a) => a.participantId));
  const results = searching
    ? (resultsQuery.data?.participants ?? []).filter((p) => !present.has(p.id))
    : [];

  const handleAdd = async (participantId: number, name: string) => {
    try {
      await add.mutateAsync({ sessionId: session.id, participantId });
      onSuccess(`Presença de ${name} registada.`);
      setSearch('');
      setDebouncedSearch('');
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'Erro ao adicionar o participante.');
    }
  };

  const handleRegistered = async (p: Participant) => {
    setRegistering(false);
    await handleAdd(p.id, `${p.nome} ${p.apelido}`);
  };

  const handleRemove = async (attendance: ActivityAttendance) => {
    try {
      await remove.mutateAsync({
        sessionId: session.id,
        participantId: attendance.participantId,
      });
      onSuccess(`Presença de ${attendance.participantName} retirada.`);
      setRemoving(null);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'Erro ao retirar o participante.');
    }
  };

  const visitors = session.visitorHeadcount ?? 0;
  const openDay = isSingleEventActivity(session.activityType);
  // Over several days the hour alone does not say which day someone came.
  const formatJoined = spansDays(session.startsAt, session.endsAt) ? formatDayTime : formatTime;

  return (
    <section className="card p-6 space-y-4" aria-labelledby="session-roster-heading">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 id="session-roster-heading" className="text-lg font-semibold text-foreground">
          Presentes
        </h2>
        <p className="text-sm text-muted-foreground tabular-nums" aria-live="polite">
          {session.attendances.length} participante{session.attendances.length !== 1 ? 's' : ''}
          {visitors > 0 && ` + ${visitors} visitante${visitors !== 1 ? 's' : ''}`}
        </p>
      </div>

      {session.attendances.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Ainda ninguém leu o código. A lista actualiza-se sozinha.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {session.attendances.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{a.participantName}</p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {formatJoined(a.joinedAt)}
                  {a.addedBy != null && ' · registada pela equipa'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setRemoving(a)}
                className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                aria-label={`Retirar ${a.participantName}`}
                title={`Retirar ${a.participantName}`}
              >
                <UserMinus className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2">
        <label htmlFor="roster-search" className="text-xs font-medium text-muted-foreground">
          Adicionar quem não consegue ler o código
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            id="roster-search"
            type="search"
            className="input pl-9 w-full"
            placeholder="Nome, apelido ou email…"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        {searching && resultsQuery.isLoading && (
          <p className="text-sm text-muted-foreground flex items-center gap-2" role="status">
            <Loader2 className="w-4 h-4 animate-spin" />
            A procurar…
          </p>
        )}
        {searching && !resultsQuery.isLoading && results.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nenhum participante encontrado fora da lista de presentes.
          </p>
        )}
        {results.length > 0 && (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {results.map((p) => {
              const name = `${p.nome} ${p.apelido}`;
              return (
                <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{name}</p>
                    <p className="text-xs text-muted-foreground">ID: {p.id}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAdd(p.id, name)}
                    disabled={add.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors shrink-0"
                    aria-label={`Adicionar ${name}`}
                  >
                    <UserPlus className="w-4 h-4" />
                    Adicionar
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {searching && !resultsQuery.isLoading && (
          <button
            type="button"
            onClick={() => setRegistering(true)}
            className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <UserPlus className="w-4 h-4" />
            Não está registado? Registar e adicionar
          </button>
        )}
      </div>

      {registering && (
        <CreateParticipantModal
          initialName={search}
          defaultCenterId={session.digitalCenterId}
          onClose={() => setRegistering(false)}
          onCreated={handleRegistered}
        />
      )}

      {removing && (
        <ConfirmDialog
          title={openDay ? 'Retirar do Dia Aberto' : 'Retirar da sessão'}
          confirmLabel="Retirar"
          isPending={remove.isPending}
          onConfirm={() => handleRemove(removing)}
          onClose={() => setRemoving(null)}
        >
          Retirar{' '}
          <span className="font-semibold text-foreground">{removing.participantName}</span>{' '}
          {openDay ? 'deste Dia Aberto' : 'desta sessão'}? Se voltar a ler o código, não entra. A
          presença pode ser reposta à mão.
        </ConfirmDialog>
      )}
    </section>
  );
}
